import { useEffect, useRef, useState } from 'react'

import { FEATURE_FLAGS_SWR_KEY } from 'api/auth/getCurrentFeatureFlags'
import getCurrentWebAppVersion from 'api/auth/getCurrentWebAppVersion'
import config from 'config'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'
import _ from 'lodash'
import useSWR, { useSWRConfig } from 'swr'

import useUser from './useUser'

const CHECK_UPDATES_INTERVAL_MS = 15 * 60 * 1000 // 15 minutes

declare global {
    interface Window {
        waffle:
            | {
                  flag_is_active: (flag: string) => boolean
                  switch_is_active: (flag: string) => boolean
              }
            | undefined
    }
}

const DEFAULT_WAFFLE = {
    flag_is_active: () => false,
    switch_is_active: () => false,
}

window.waffle = DEFAULT_WAFFLE

async function isWebAppNewVersion(): Promise<boolean> {
    if (config.ENVIRONMENT !== 'local') {
        try {
            const response = await getCurrentWebAppVersion()

            if (response && response.build !== '' && response.build !== null) {
                const buildVersion = String(response.build)

                const releaseVersion = String(
                    process.env.REACT_APP_RELEASE_VERSION,
                )

                return buildVersion !== releaseVersion
            }
        } catch (error) {
            OctaneLogger.error(
                'Error getting deployment json',
                undefined,
                error as Error,
            )
        }
    }

    return false
}

function useCurrentFlags(
    isLoggedIn: boolean,
): [boolean, React.Dispatch<React.SetStateAction<boolean>>] {
    const [currentFlags, setCurrentFlags] = useState({})
    const [isFlagOrSwitchUpdated, setIsFlagOrSwitchUpdated] = useState(false)

    // Read from the SWR cache populated by FeatureFlagContextProvider.
    // Passing null as the fetcher prevents SWR from making its own HTTP request —
    // this hook is a cache observer only; FeatureFlagContextProvider owns fetching.
    // Key is null when logged out so stale cache data is never applied to window.waffle.
    const { data } = useSWR(isLoggedIn ? FEATURE_FLAGS_SWR_KEY : null, null, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    })

    // Reset window.waffle to the default no-op when the user logs out so stale
    // feature-flag data from the SWR cache is never applied after logout.
    useEffect(() => {
        if (!isLoggedIn) {
            window.waffle = DEFAULT_WAFFLE
        }
    }, [isLoggedIn])

    useEffect(() => {
        if (!data) return

        const remoteFlags = { ...data.flags, ...data.switches }

        window.waffle = {
            flag_is_active: (name: string) => !!remoteFlags[name]?.is_active,
            switch_is_active: (name: string) => !!remoteFlags[name]?.is_active,
        }

        if (
            !_.isEqualWith(
                currentFlags,
                remoteFlags,
                (_value1, _value2, key) => {
                    return key === 'last_modified' ? true : undefined
                },
            )
        ) {
            setCurrentFlags(remoteFlags)

            if (Object.keys(currentFlags).length > 0) {
                setIsFlagOrSwitchUpdated(true)
            }
        } else {
            setIsFlagOrSwitchUpdated(false)
        }
        // currentFlags is intentionally excluded: we only want to react to
        // fresh data arriving from SWR, not re-run on every local state update.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    return [isFlagOrSwitchUpdated, setIsFlagOrSwitchUpdated]
}

export default function useKeepAppUpToDate(
    renderNotification: (value: boolean) => void,
    setShouldUpdateFlags?: () => void,
) {
    const { isLoggedIn } = useUser()
    const { mutate } = useSWRConfig()

    const [isFlagOrSwitchUpdated, setIsFlagOrSwitchUpdated] = useCurrentFlags(
        isLoggedIn ?? false,
    )

    const timeoutId = useRef<NodeJS.Timeout | null>(null)

    // Fires immediately when flags change — avoids the stale-closure problem of
    // reading isFlagOrSwitchUpdated inside the async poll callback.
    useEffect(() => {
        if (isFlagOrSwitchUpdated) {
            renderNotification(true)
            if (setShouldUpdateFlags) {
                setShouldUpdateFlags()
            }

            setIsFlagOrSwitchUpdated(false)
        }
        // renderNotification and setShouldUpdateFlags are stable callback props;
        // including them would cause unnecessary re-runs without benefit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFlagOrSwitchUpdated])

    useEffect(() => {
        if (!isLoggedIn) return

        // On mount, FeatureFlagContextProvider has already fetched flags via SWR.
        // Skip the immediate mutate/version-check and only start the polling timer.
        // Each tick revalidates the SWR cache (one shared HTTP request) and checks
        // for a new app version.
        const poll = async () => {
            if (timeoutId.current) {
                clearTimeout(timeoutId.current)
                timeoutId.current = null
            }

            if (!document.hidden) {
                // Force a fresh fetch; both this hook and FeatureFlagContextProvider
                // share the same SWR key so only one HTTP request is made.
                await mutate(FEATURE_FLAGS_SWR_KEY)

                const isANewVersion = await isWebAppNewVersion()

                if (isANewVersion) {
                    renderNotification(true)
                    if (setShouldUpdateFlags) {
                        setShouldUpdateFlags()
                    }
                }
            }

            timeoutId.current = setTimeout(poll, CHECK_UPDATES_INTERVAL_MS)
        }

        timeoutId.current = setTimeout(poll, CHECK_UPDATES_INTERVAL_MS)

        return () => {
            if (timeoutId.current) {
                clearTimeout(timeoutId.current)
                timeoutId.current = null
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, mutate])
}

export const testExports = {
    isWebAppNewVersion,
    useCurrentFlags,
}
